// Timeline de intentos de puja de un comprador

import React, { useState } from 'react';
import { DollarSign, AlertTriangle, CheckCircle, XCircle, Clock, Edit, ChevronDown, ChevronUp } from 'lucide-react';
import { groupBiddingAttempts, determineErrorMessage, getAttemptStatusConfig } from '../../../utils/biddingAttemptsUtils';
import { formatTimeAgo, formatTimeWithSeconds, getTimeAgoColor } from '../../../utils/engagementTimeFormatters';

/**
 * Timeline de intentos de puja
 * Agrupa eventos de bidding en intentos coherentes
 */
export const BiddingAttemptTimeline = ({ events, buyer }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Agrupar eventos en intentos de puja
  const attempts = groupBiddingAttempts(events);

  if (attempts.length === 0) {
    return null;
  }

  // Invertir orden: más reciente primero
  const reversedAttempts = [...attempts].reverse();
  const visibleAttempts = reversedAttempts.slice(0, 3);
  const olderAttempts = reversedAttempts.slice(3);

  return (
    <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-amber-400" />
        Intentos de Puja
        <span className="text-slate-400 text-sm font-normal">
          ({attempts.length} intento{attempts.length !== 1 ? 's' : ''})
        </span>
      </h3>

      <div className="space-y-4">
        {/* Últimos 3 intentos (siempre visibles) */}
        {visibleAttempts.map((attempt, idx) => (
          <BiddingAttemptCard 
            key={idx}
            attempt={attempt}
            index={attempts.length - idx}
            isLatest={idx === 0}
          />
        ))}

        {/* Intentos anteriores (colapsables) */}
        {olderAttempts.length > 0 && (
          <>
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm text-slate-400 hover:text-amber-400 transition-colors"
            >
              {expanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Ocultar {olderAttempts.length} intento{olderAttempts.length !== 1 ? 's' : ''} anterior{olderAttempts.length !== 1 ? 'es' : ''}
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Ver {olderAttempts.length} intento{olderAttempts.length !== 1 ? 's' : ''} anterior{olderAttempts.length !== 1 ? 'es' : ''}
                </>
              )}
            </button>

            {expanded && (
              <div className="space-y-4">
                {olderAttempts.map((attempt, idx) => (
                  <BiddingAttemptCard 
                    key={idx}
                    attempt={attempt}
                    index={attempts.length - 1 - idx}
                    isLatest={false}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Tarjeta de intento individual
 */
const BiddingAttemptCard = ({ attempt, index, isLatest = false }) => {
  const lastAmount = attempt.amounts[attempt.amounts.length - 1];
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    return `${Math.round(seconds / 60)}m`;
  };

  // Determinar estilo según estado
  const getStatusStyle = () => {
    switch (attempt.status) {
      case 'success':
        return {
          bg: 'bg-green-500/10',
          border: 'border-green-500/50',
          icon: CheckCircle,
          iconColor: 'text-green-400',
          label: 'Completado',
        };
      case 'failed':
        return {
          bg: 'bg-red-500/10',
          border: 'border-red-500/50',
          icon: XCircle,
          iconColor: 'text-red-400',
          label: 'Error',
        };
      case 'abandoned':
        return {
          bg: 'bg-amber-500/10',
          border: 'border-amber-500/50',
          icon: AlertTriangle,
          iconColor: 'text-amber-400',
          label: 'Abandonado',
        };
      case 'pending_verification':
        return {
          bg: 'bg-blue-500/10',
          border: 'border-blue-500/50',
          icon: Clock,
          iconColor: 'text-blue-400',
          label: 'Sin verificar en email',
        };
      case 'expired':
        return {
          bg: 'bg-orange-500/10',
          border: 'border-orange-500/50',
          icon: Clock,
          iconColor: 'text-orange-400',
          label: 'Expirado',
        };
      default:
        return {
          bg: 'bg-slate-700/30',
          border: 'border-slate-600',
          icon: Clock,
          iconColor: 'text-slate-400',
          label: 'En curso',
        };
    }
  };

  const style = getStatusStyle();
  const StatusIcon = style.icon;

  return (
    <div className={`${style.bg} border ${style.border} rounded-lg p-4`}>
      {/* Header del intento */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <StatusIcon className={`w-5 h-5 ${style.iconColor}`} />
          <div>
            <div className="font-semibold text-white">
              Intento #{index + 1}
            </div>
            <div className="text-xs text-slate-400">
              {attempt.status === 'success' && attempt.verifiedAt ? (
                <>Verificada: {formatTimeWithSeconds(attempt.verifiedAt)}</>
              ) : (
                <>{formatTimeAgo(attempt.startedAt)} ({formatTimeWithSeconds(attempt.startedAt)})</>
              )}
            </div>
          </div>
        </div>

        <span className={`px-2 py-1 rounded text-xs font-medium ${style.iconColor}`}>
          {style.label}
        </span>
      </div>

      {/* Detalles del intento */}
      <div className="space-y-2 text-sm">
        {/* Cantidad */}
        {lastAmount && (
          <div className="flex items-center gap-2 text-slate-300">
            <DollarSign className="w-4 h-4 text-slate-500" />
            <span>Cantidad escrita:</span>
            <span className="font-semibold text-white">
              €{lastAmount.toLocaleString()}
            </span>
            {attempt.amountChanges > 1 && (
              <span className="text-slate-500">
                (cambió {attempt.amountChanges} veces)
              </span>
            )}
          </div>
        )}

        {/* Condiciones */}
        {attempt.conditions.length > 0 && (
          <div className="flex items-start gap-2 text-slate-300">
            <CheckCircle className="w-4 h-4 text-slate-500 mt-0.5" />
            <span>Condiciones:</span>
            <span className="text-white">
              {attempt.conditions.join(', ')}
            </span>
          </div>
        )}

        {/* Texto custom */}
        {attempt.customText && (
          <div className="flex items-center gap-2 text-slate-300">
            <Edit className="w-4 h-4 text-slate-500" />
            <span>Agregó condiciones personalizadas</span>
          </div>
        )}

        {/* Tiempo invertido */}
        {attempt.timeSpent > 0 && (
          <div className="flex items-center gap-2 text-slate-300">
            <Clock className="w-4 h-4 text-slate-500" />
            <span>Tiempo invertido:</span>
            <span className="text-white font-medium">
              {formatTime(attempt.timeSpent)}
            </span>
          </div>
        )}

        {/* Razón de fallo/abandono */}
        {attempt.reason && attempt.status !== 'success' && (
          <div className={`flex items-start gap-2 mt-3 p-2 rounded ${
            attempt.status === 'failed' ? 'bg-red-500/10' : 'bg-amber-500/10'
          }`}>
            <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
              attempt.status === 'failed' ? 'text-red-400' : 'text-amber-400'
            }`} />
            <span className={attempt.status === 'failed' ? 'text-red-300' : 'text-amber-300'}>
              {attempt.reason}
            </span>
          </div>
        )}

        {/* Éxito */}
        {attempt.status === 'success' && (
          <div className="flex items-start gap-2 mt-3 p-2 rounded bg-green-500/10">
            <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
            <span className="text-green-300">
              ✅ Oferta de €{attempt.finalAmount?.toLocaleString()} completada exitosamente
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BiddingAttemptTimeline;